import { Drawer } from "@mui/material";
import {
  Menu,
  X,
  ArrowRight,
  ShieldCheck,
  MessageCircle,
  Stethoscope,
  Sparkles,
} from "lucide-react";
import React, { useState, useEffect } from "react";
import Login from "../auth/Login";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const Header = () => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openLogin, setOpenLogin] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const navigate = useNavigate();

  const WHATSAPP_SUPPORT = "https://wa.me/2348077417209";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleScrollTo = (id) => {
    const el = document.getElementById(id);

    if (el) {
      const yOffset = -92;
      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const navItems = [
    { name: "How it works", id: "how-it-works" },
    { name: "Features", id: "feature" },
    { name: "Pricing", id: "pricing" },
    { name: "Contact", id: "contact" },
  ];

  const closeDrawerAndScroll = (id) => {
    setOpenDrawer(false);
    setTimeout(() => handleScrollTo(id), 220);
  };

  return (
    <>
      {/* ================= HEADER ================= */}
      <header
        className={`fixed left-0 top-0 z-50 w-full transition-all duration-300 ${
          scrolled
            ? "border-b border-emerald-100/80 bg-white/92 shadow-[0_18px_45px_rgba(15,23,42,0.08)] backdrop-blur-2xl"
            : "border-b border-white/40 bg-white/68 backdrop-blur-xl"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          {/* LOGO */}
          <button
            type="button"
            onClick={() => navigate("/")}
            className="group flex min-w-0 items-center gap-3 rounded-2xl pr-2 text-left transition active:scale-[0.99]"
          >
            <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ">
              <img
                src="/zavira_nobg.png"
                alt="Zaviramed"
                className="h-9 w-9 object-contain transition duration-300 group-hover:scale-105"
              />

            </div>

            <div className="min-w-0">
              <p className="truncate text-base font-black tracking-[-0.03em] text-slate-950 sm:text-lg">
                Zavira<span className="text-emerald-600">Med</span>
              </p>
              <p className="-mt-0.5 hidden truncate text-[11px] font-bold text-slate-500 sm:block">
                WhatsApp-first healthcare
              </p>
            </div>
          </button>

          {/* DESKTOP NAV */}
          <nav className="hidden items-center rounded-full border border-emerald-100 bg-emerald-50/50 p-1 lg:flex">
            {navItems.map((item) => (
              <button
                key={item.name}
                type="button"
                onClick={() => handleScrollTo(item.id)}
                className="rounded-full px-4 py-2 text-sm font-extrabold text-slate-600 transition hover:bg-white hover:text-emerald-700 hover:shadow-sm"
              >
                {item.name}
              </button>
            ))}
          </nav>

          {/* ACTIONS */}
          <div className="hidden items-center gap-3 lg:flex">
            <button
              type="button"
              onClick={() => setOpenLogin(true)}
              className="rounded-full px-4 py-2 text-sm font-extrabold text-slate-700 transition hover:bg-slate-100 hover:text-slate-950"
            >
              Doctor Sign In
            </button>

            <button
              type="button"
              onClick={() => navigate("/register-as-doctor")}
              className="group inline-flex items-center gap-2 rounded-full bg-[#075E54] px-5 py-2.5 text-sm font-black text-white shadow-[0_16px_35px_rgba(7,94,84,0.24)] transition hover:-translate-y-0.5 hover:bg-emerald-700"
            >
              Join as Doctor
              <ArrowRight
                size={16}
                className="transition group-hover:translate-x-0.5"
              />
            </button>
          </div>

          {/* MOBILE MENU */}
          <button
            type="button"
            onClick={() => setOpenDrawer(true)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-emerald-100 bg-white text-slate-900 shadow-sm transition active:scale-95 lg:hidden"
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>
        </div>
      </header>

      {/* ================= MOBILE DRAWER ================= */}
      <Drawer
        anchor="left"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        PaperProps={{
          sx: {
            borderTopRightRadius: "28px",
            borderBottomRightRadius: "28px",
            overflow: "hidden",
          },
        }}
      >
        <div className="flex h-full w-[88vw] max-w-[350px] flex-col bg-[#f7fbf8]">
          {/* TOP BRAND */}
          <div className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-cyan-50 px-5 pb-6 pt-6">
            <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-12 left-8 h-28 w-28 rounded-full bg-cyan-200/30 blur-2xl" />

            <div className="relative flex items-start justify-between gap-4">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-emerald-100">
                  <img
                    src="/zavira_nobg.png"
                    alt="Zaviramed"
                    className="h-9 w-9 object-contain"
                  />
                </div>

                <div className="min-w-0">
                  <h2 className="truncate text-lg font-black tracking-[-0.03em] text-slate-950">
                    Zavira<span className="text-emerald-600">Med</span>
                  </h2>
                  <p className="mt-0.5 text-xs font-bold text-slate-500">
                    Doctor portal access
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setOpenDrawer(false)}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-sm ring-1 ring-slate-100 active:scale-95"
                aria-label="Close menu"
              >
                <X size={19} />
              </button>
            </div>

            <div className="relative mt-5 rounded-3xl border border-emerald-100 bg-white/85 p-4 shadow-sm backdrop-blur">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#25D366]/12 text-[#075E54]">
                  <MessageCircle size={20} />
                </div>

                <div>
                  <p className="text-sm font-black text-slate-950">
                    WhatsApp-themed care
                  </p>
                  <p className="mt-0.5 text-xs font-semibold leading-relaxed text-slate-500">
                    Manage patients, messages, and consultations faster.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* NAV ITEMS */}
          <div className="flex-1 overflow-y-auto px-4 py-5">
            <p className="px-2 text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">
              Navigate
            </p>

            <div className="mt-3 flex flex-col gap-2">
              {navItems.map((item, i) => (
                <motion.button
                  key={item.name}
                  type="button"
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.045 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => closeDrawerAndScroll(item.id)}
                  className="group flex w-full items-center justify-between rounded-2xl border border-white bg-white px-4 py-3.5 text-left shadow-sm transition hover:border-emerald-100 hover:bg-emerald-50"
                >
                  <span className="text-sm font-black text-slate-700 group-hover:text-emerald-800">
                    {item.name}
                  </span>

                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-50 text-slate-400 group-hover:bg-white group-hover:text-emerald-600">
                    ›
                  </span>
                </motion.button>
              ))}
            </div>

            <div className="mt-5 rounded-3xl border border-emerald-100 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2 text-sm font-black text-slate-950">
                <ShieldCheck size={18} className="text-emerald-600" />
                Secure doctor access
              </div>

              <p className="mt-2 text-xs font-semibold leading-relaxed text-slate-500">
                Built for verified doctors to manage patient conversations with
                clarity and speed.
              </p>
            </div>
          </div>

          {/* ACTIONS */}
          <div className="border-t border-emerald-100 bg-white px-5 py-5">
            <button
              type="button"
              onClick={() => {
                setOpenDrawer(false);
                setOpenLogin(true);
              }}
              className="flex h-12 w-full items-center justify-center rounded-2xl border border-emerald-200 bg-white text-sm font-black text-emerald-700 transition active:scale-[0.99]"
            >
              Doctor Sign In
            </button>

            <button
              type="button"
              onClick={() => {
                setOpenDrawer(false);
                navigate("/register-as-doctor");
              }}
              className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-[#075E54] text-sm font-black text-white shadow-[0_14px_30px_rgba(7,94,84,0.22)] transition active:scale-[0.99]"
            >
              Join as Doctor
              <ArrowRight size={16} />
            </button>

            <p className="mt-4 text-center text-xs font-semibold text-slate-500">
              Need help?{" "}
              <button
                type="button"
                onClick={() => window.open(WHATSAPP_SUPPORT, "_blank")}
                className="font-black text-emerald-600 hover:underline"
              >
                Chat on WhatsApp
              </button>
            </p>
          </div>
        </div>
      </Drawer>

      {/* ================= LOGIN DRAWER ================= */}
      <Drawer
        anchor="right"
        open={openLogin}
        onClose={() => setOpenLogin(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: { xs: 0, sm: "28px" },
            borderBottomLeftRadius: { xs: 0, sm: "28px" },
            overflow: "hidden",
          },
        }}
      >
        <div className="flex h-full w-[100vw] flex-col bg-white sm:w-[92vw] sm:max-w-[440px]">
          {/* TOP BRAND */}
          <div className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-cyan-50 px-6 pb-6 pt-7">
            <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-emerald-200/35 blur-2xl" />

            <div className="relative flex items-start justify-between gap-4">
                <div></div>

              <button
                type="button"
                onClick={() => setOpenLogin(false)}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-sm ring-1 ring-slate-100 active:scale-95"
                aria-label="Close login"
              >
                <X size={19} />
              </button>
            </div>

         
          </div>

          {/* LOGIN */}
          <div className="flex-1 overflow-y-auto px-5 py-6 sm:px-6">
            <Login />
          </div>

          {/* TRUST / FOOT */}
          
        </div>
      </Drawer>
    </>
  );
};

export default Header;